// @need-translate
import {useCallback, useEffect, useState} from 'react';
import {checkIfContentHomeScheme} from '../../../../common/checkIfContentHomeScheme';
import {t} from '../../../../common/getTranslations';
import {openWindow} from '../../../../common/openWindow.function';
import {requestContext} from '../../../../common/requestContext';
import {getPagewebUrl} from '../../../../common/shared/scheme/getPagewebUrl.function';
import {PagewebUrl} from '../../../../common/shared/scheme/PagewebUrl.class';
import {getDataset} from '../../../../common/shared/utils/getDataset';
import {useDialog} from '../../../../dialog/useDialog.hook';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {EventPageV2} from '../presentational/EventPageV2';
import {EventPageV2ContainerProps} from '../type';

export function EventPageV2Container({
  isAlert,
  alertMessage,
  isNeedUpdate,
  contentUrl,
  contentInfoList,
}: EventPageV2ContainerProps) {
  const {isMobile, isIos, isAndroid, isWebView} =
    requestContext.requestInfo.display;
  const [dialogMessage, setDialogMessage] = useState('');
  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });

  useEffect(() => {
    if (isAlert) {
      setDialogMessage(alertMessage || '알 수 없는 오류 발생');
      showDialog();
    }
  }, [isAlert, alertMessage, showDialog]);

  const onClickContent = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      const androidScheme = getDataset(e.currentTarget, 'schemeAndroid');
      const iosScheme = getDataset(e.currentTarget, 'schemeIos');
      const webScheme = getDataset(e.currentTarget, 'schemeWeb');
      const scheme = isWebView
        ? isIos
          ? iosScheme
          : isAndroid
            ? androidScheme
            : undefined
        : webScheme;

      const {isContentHome, seriesId} = checkIfContentHomeScheme(scheme);

      const sendLog = (url: string) =>
        aggregateAndSendLog({
          targetElem: e.target as HTMLElement,
          actionType: 'Event',
          actionName: `${isContentHome ? '작품' : '버튼'}_클릭`,
          eventMeta: {
            id: seriesId ?? (scheme as string),
            type: isContentHome ? 'seriesId' : 'scheme',
            provider: '서비스',
            series_id: seriesId,
          },
          customProps: {
            user_action: 'click',
            landing_path: url,
          },
        });

      // 스킴이 존재하고 웹뷰 환경이 아닌 경우(모웹 & PC웹), 페이지웹 URL로의 변환이 필요함.
      // 에러 발생 시 아무 동작을 하지 않습니다.
      if (!isWebView) {
        try {
          if (scheme) {
            const _url = getPagewebUrl({
              appScheme: scheme,
            });
            if (_url instanceof PagewebUrl) {
              sendLog(_url.fullUrl);
              if (_url.openNewWindow) {
                openWindow({
                  url: _url.fullUrl,
                  target: _url.fullUrl,
                  features: 'width=460, height=700',
                });
              } else {
                location.href = _url.fullUrl;
              }
              return;
            } else {
              sendLog(_url);
              location.href = _url;
              return;
            }
          }
          // scheme 정보가 없다면 아무 동작을 하지 않습니다. (웹 미지정인 경우)
        } catch {}
        return;
      }

      if (isNeedUpdate) {
        setDialogMessage(
          '앱을 업데이트 해야 이용하실 수 있어요.\n업데이트 후 다시 실행해주세요.'
        );
        showDialog();
        return;
      }

      // 웹뷰 환경인 경우 location.href를 스킴 그대로 할당합니다.
      // 스킴 자체가 존재하지 않는다면 아무 동작을 하지 않습니다.
      if (isIos && iosScheme) {
        sendLog(iosScheme);
        location.href = iosScheme;
        return;
      } else if (isAndroid && androidScheme) {
        sendLog(androidScheme);
        location.href = androidScheme;
        return;
      }
    },
    [isIos, isAndroid, isWebView, isNeedUpdate, showDialog]
  );

  return (
    <>
      <div className="flex h-full w-full grow flex-col items-center">
        <EventPageV2
          isAlert={isAlert}
          contentUrl={contentUrl}
          contentInfoList={contentInfoList}
          onClickContent={onClickContent}
          className="h-full w-full max-w-720pxr"
          {...putTiaraObjectProps({
            click: {
              layer1: '이벤트버튼',
            },
          })}
        />
      </div>
      <DialogWrapper>
        <Dialog.Dimmed />
        <Dialog.Title>{dialogMessage}</Dialog.Title>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={hideDialog}>
            {t('common_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </DialogWrapper>
    </>
  );
}
